import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ProfilePDF from '../assets/ABRAJ_AL_DHAFNA_PROFILE.pdf';
// Note: Ensure the file extension .pdf is included in the filename
import { 
  FaBars, 
  FaTimes, 
  FaPhone, 
  FaEnvelope, 
  FaWhatsapp,
  FaArrowRight
} from 'react-icons/fa';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.header')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home', icon: <FaArrowRight /> },
    { path: '/about', label: 'About Us', icon: <FaArrowRight /> },
    { path: '/services', label: 'Services', icon: <FaArrowRight /> },
    { path: '/projects', label: 'Projects', icon: <FaArrowRight /> },
    { path: '/blog', label: 'Blog', icon: <FaArrowRight /> },
    { path: '/gallery', label: 'Gallery', icon: <FaArrowRight /> },
    { path: '/contact', label: 'Contact', icon: <FaArrowRight /> }
  ];

  const handleContactClick = (type) => {
    switch (type) {
      case 'phone':
        window.open('tel:+97444170859', '_self');
        break;
      case 'email':
        window.open('mailto:abrajaldhafna2014@gmail.com', '_self');
        break;
      case 'whatsapp':
        window.open('https://wa.me/97455994434', '_blank');
        break;
      default:
        break;
    }
  };

  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  return (
    <>
      <a href="#main-content" className="skip-link" style={{
        position: 'absolute',
        left: '-9999px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        zIndex: 1000,
        background: '#fff',
        color: '#1a2233',
        padding: '8px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }} onFocus={(e) => {
        e.target.style.left = '8px';
        e.target.style.top = '8px';
        e.target.style.width = 'auto';
        e.target.style.height = 'auto';
        e.target.style.overflow = 'visible';
      }} onBlur={(e) => {
        e.target.style.left = '-9999px';
        e.target.style.top = 'auto';
        e.target.style.width = '1px';
        e.target.style.height = '1px';
        e.target.style.overflow = 'hidden';
      }}>
        Skip to main content
      </a>
      
      <motion.header 
        className={`header ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        role="banner"
        aria-label="Main navigation"
      >
        <div className="container">
          <div className="header-content">
            {/* Logo */}
            <Link to="/" className="logo" aria-label="Go to homepage">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <img 
                  src={require('../assets/full_logo.jpeg')} 
                  alt="Abraj Al Dhafna Trading & Contracting W.L.L Logo" 
                  className="header-logo-image" 
                  style={{height: '48px', width: 'auto'}} 
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="nav-desktop" role="navigation" aria-label="Main navigation">
              <ul className="nav-list" role="list">
                {navItems.map((item, index) => (
                  <motion.li 
                    key={item.path}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link 
                      to={item.path}
                      className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                      aria-label={`Navigate to ${item.label} page`}
                      aria-current={location.pathname === item.path ? 'page' : undefined}
                    >
                      {item.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </nav>

            {/* Contact Buttons */}
            <div className="header-actions" role="group" aria-label="Contact actions">
              <motion.button
                className="contact-btn phone-btn"
                onClick={() => handleContactClick('phone')}
                onKeyDown={(e) => handleKeyDown(e, () => handleContactClick('phone'))}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                title="Call us at +97444170859"
                aria-label="Call us"
              >
                <FaPhone />
                <span className="sr-only">Call</span>
              </motion.button>
              
              <motion.button
                className="contact-btn whatsapp-btn"
                onClick={() => handleContactClick('whatsapp')}
                onKeyDown={(e) => handleKeyDown(e, () => handleContactClick('whatsapp'))}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
                title="Contact us on WhatsApp"
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
                <span className="sr-only">WhatsApp</span>
              </motion.button>

              {/* PDF Link Button */}
{/* PDF Link Button */}
<motion.a
  href={ProfilePDF}
  target="_blank"
  rel="noopener noreferrer"
  className="btn btn-primary cta-btn"
  style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    textDecoration: 'none',
    gap: '8px' // Adds space if you decide to add an icon later
  }}
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  transition={{ duration: 0.2 }}
  aria-label="View Company Profile PDF"
>
  Company Profile
</motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              onKeyDown={(e) => handleKeyDown(e, () => setIsMenuOpen(!isMenuOpen))}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              id="mobile-navigation"
              className="mobile-nav"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              role="navigation"
              aria-label="Mobile navigation"
            >
              <div className="container">
                <nav className="nav-mobile">
                  <ul className="mobile-nav-list" role="list">
                    {navItems.map((item, index) => (
                      <motion.li 
                        key={item.path}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Link 
                          to={item.path}
                          className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                          aria-label={`Navigate to ${item.label} page`}
                          aria-current={location.pathname === item.path ? 'page' : undefined}
                        >
                          {item.label}
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                  
                  <div className="mobile-contact" role="group" aria-label="Mobile contact actions">
  {/* Call Now Button */}
  <motion.button
    className="btn btn-primary btn-lg"
    onClick={() => handleContactClick('phone')}
    onKeyDown={(e) => handleKeyDown(e, () => handleContactClick('phone'))}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    transition={{ duration: 0.2 }}
    aria-label="Call us now"
  >
    <FaPhone />
    Call Now
  </motion.button>
  
  {/* WhatsApp Button */}
  <motion.button
    className="btn btn-secondary btn-lg"
    onClick={() => handleContactClick('whatsapp')}
    onKeyDown={(e) => handleKeyDown(e, () => handleContactClick('whatsapp'))}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    transition={{ duration: 0.2 }}
    aria-label="Contact us on WhatsApp"
  >
    <FaWhatsapp />
    WhatsApp
  </motion.button>

  {/* ADDED BELOW: Company Profile PDF Button */}
  <motion.a
    href={ProfilePDF}
    target="_blank"
    rel="noopener noreferrer"
    className="btn btn-primary btn-lg profile-mobile-btn"
    style={{ 
      textDecoration: 'none', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: '10px', 
      marginTop: '10px',
      width: '100%',
      backgroundColor: '#1a2233' // You can change this to match your theme
    }}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    aria-label="View Company Profile PDF"
  >
    <FaArrowRight /> 
    Company Profile
  </motion.a>
</div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default Header; 